import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface FirmwareVersionHistoryProps {
  apiBaseUrl: string;
}

interface VersionDataPoint {
  version: string;
  count: number;
}

interface HistoricalDataPoint {
  date: string;
  totalDevices: number;
  versionData: VersionDataPoint[];
  [key: string]: any; // Allow dynamic version properties
}

type TimeRange = {
  value: number;
  label: string;
};

// Color palette - vibrant colors that work well together
const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', 
  '#82CA9D', '#8DD1E1', '#A4DE6C', '#D0ED57', '#FFC658',
  '#F66D44', '#FEAE65', '#E6F69D', '#AADEA7', '#64C2A6'
];

// Grey color specifically for "Other" category
const OTHER_COLOR = '#AAAAAA';

// Helper function to format version with 'v' prefix
const formatVersion = (version: string): string => {
  if (!version) return 'Unknown';
  if (version === 'Other') return 'Other';
  
  // If version already starts with 'v' or 'V', don't add another one
  if (version.match(/^[vV]/)) {
    return version;
  }
  return `v${version}`;
};

const FirmwareVersionHistory: React.FC<FirmwareVersionHistoryProps> = ({ apiBaseUrl }) => {
  const [historicalData, setHistoricalData] = useState<HistoricalDataPoint[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRange, setSelectedRange] = useState<TimeRange>({ value: 30, label: '1 Month' });
  const [topVersionCount, setTopVersionCount] = useState<number>(5);
  const [displayVersions, setDisplayVersions] = useState<string[]>([]);
  const [showOther, setShowOther] = useState<boolean>(false);

  // Define available time ranges
  const timeRanges: TimeRange[] = [
    { value: 7, label: '1 Week' },
    { value: 30, label: '1 Month' },
    { value: 90, label: '3 Months' },
    { value: 180, label: '6 Months' },
    { value: 365, label: '1 Year' },
  ];

  useEffect(() => {
    fetchVersionHistory();
  }, [apiBaseUrl, selectedRange, topVersionCount]);

  const fetchVersionHistory = async () => {
    setLoading(true);
    try {
      const params = {
        days: selectedRange.value,
        top: topVersionCount
      };
      
      const response = await axios.get(`${apiBaseUrl}/devices/version-metrics/history`, {
        params,
        withCredentials: true
      });
      
      if (response.data && response.data.history) {
        // Process the historical data
        const formattedData = processHistoricalData(response.data.history);
        setHistoricalData(formattedData);
        
        // Extract versions to display based on the first data point
        // This assumes the backend already sorted versions by popularity
        if (formattedData.length > 0) {
          const latestDataPoint = formattedData[formattedData.length - 1];
          const versions = latestDataPoint.versionData
            .slice(0, topVersionCount)
            .map(v => v.version);
          
          setDisplayVersions(versions);
        }
        
        setError(null);
      } else {
        throw new Error("Invalid response format from version history endpoint");
      }
    } catch (error: any) {
      console.error('Error fetching version history data:', error);
      setError('Failed to load firmware version history data');
      
      // Use mock data for development/fallback
      const mockData = generateMockHistoricalData();
      setHistoricalData(mockData);
      
      // Extract versions from mock data
      if (mockData.length > 0) {
        const latestDataPoint = mockData[mockData.length - 1];
        const versions = latestDataPoint.versionData
          .slice(0, topVersionCount)
          .map(v => v.version);
        
        setDisplayVersions(versions);
      }
    } finally {
      setLoading(false);
    }
  };

  // Process historical data for chart display
  const processHistoricalData = (historyData: any[]): HistoricalDataPoint[] => {
    return historyData.map(item => {
      // Format the date for display
      const date = new Date(item.date);
      const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      // Process version data
      const versionData = item.versionData.map((v: any) => ({
        version: v.version,
        displayName: formatVersion(v.version),
        count: v.count,
      }));
      
      // Sort by count (descending), with "Other" always at the end
      versionData.sort((a: any, b: any) => {
        if (a.version === 'Other') return 1;
        if (b.version === 'Other') return -1;
        return b.count - a.count;
      });
      
      return {
        date: formattedDate,
        totalDevices: item.totalDevices,
        versionData: versionData,
        // Add individual version counts as properties for recharts
        ...versionData.reduce((acc: any, v: any) => {
          acc[v.version] = v.count;
          return acc;
        }, {})
      };
    });
  };

  // Generate mock historical data
  const generateMockHistoricalData = (): HistoricalDataPoint[] => {
    const data: HistoricalDataPoint[] = [];
    const now = new Date();
    const days = selectedRange.value;
    
    // Define some initial version counts
    const versions = [
      { version: '1.2.4', count: 2500 },
      { version: '1.2.3', count: 1200 },
      { version: '1.2.1', count: 800 },
      { version: '1.1.0', count: 500 },
      { version: '1.0.5', count: 250 },
      { version: 'Other', count: 190 }
    ];
    
    let totalDevices = versions.reduce((sum, v) => sum + v.count, 0);
    
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(now.getDate() - (days - i - 1));
      const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      // Create a copy of the versions array for this day
      const dayVersions = [...versions].map((v, index) => {
        let count = v.count;
        
        // Add some variance to each version over time
        const dayFactor = i / days; // 0 to 1 as time progresses
        
        if (v.version === '1.2.4') {
          // Newest version, growing over time
          count = Math.round(500 + 2000 * dayFactor + (Math.random() * 100 - 50));
        } else if (v.version === '1.2.3') {
          // Older version, declining slightly
          count = Math.round(1500 - 300 * dayFactor + (Math.random() * 80 - 40));
        } else if (v.version === '1.2.1') {
          // Older version, declining slightly
          count = Math.round(1000 - 200 * dayFactor + (Math.random() * 60 - 30));
        } else if (v.version === '1.1.0') {
          // Much older version, declining more
          count = Math.round(800 - 300 * dayFactor + (Math.random() * 40 - 20));
        } else if (v.version === '1.0.5') {
          // Very old version, declining rapidly
          count = Math.round(400 - 150 * dayFactor + (Math.random() * 30 - 15));
        } else if (v.version === 'Other') {
          // Other versions, declining slightly
          count = Math.round(250 - 60 * dayFactor + (Math.random() * 20 - 10));
        }
        
        // Ensure count is not negative
        count = Math.max(0, count);
        
        return {
          version: v.version,
          count: count
        };
      });
      
      // Recalculate total for this day
      const dayTotal = dayVersions.reduce((sum, v) => sum + v.count, 0);
      
      // Create the day's data point
      const dayData: any = {
        date: formattedDate,
        totalDevices: dayTotal,
        versionData: dayVersions,
      };
      
      // Add individual version counts as properties for recharts
      dayVersions.forEach(v => {
        dayData[v.version] = v.count;
      });
      
      data.push(dayData);
    }
    
    return data;
  };

  const handleRangeChange = (range: TimeRange) => {
    setSelectedRange(range);
  };

  const handleTopVersionsChange = (count: number) => {
    setTopVersionCount(count);
  };

  const getTickInterval = () => {
    if (historicalData.length > 60) return Math.floor(historicalData.length / 15);
    if (historicalData.length > 30) return Math.floor(historicalData.length / 10);
    return 0; // Show all ticks for smaller datasets
  };

  const getVersionColor = (version: string, index: number) => {
    if (version === 'Other') return OTHER_COLOR;
    return COLORS[index % COLORS.length];
  };

  // Custom tooltip formatter
  const tooltipFormatter = (value: number, name: string, props: any) => {
    // If the name is a version, format it nicely
    let displayName = name;
    if (name !== 'date' && name !== 'totalDevices') {
      displayName = formatVersion(name);
      
      // Calculate percentage
      const totalDevices = props.payload.totalDevices || 0;
      const percentage = totalDevices > 0 ? ((value / totalDevices) * 100).toFixed(1) : 0;
      
      return [`${value.toLocaleString()} devices (${percentage}%)`, displayName];
    }
    
    return [value, displayName];
  };

  // For the legend
  const renderCustonLegend = () => {
    // Get the latest data point to show current percentages
    const latestData = historicalData.length > 0 ? historicalData[historicalData.length - 1] : null;
    
    if (!latestData) return null;
    
    return (
      <div className="d-flex flex-wrap justify-content-center mb-3">
        {displayVersions.map((version, index) => {
          const versionData = latestData.versionData.find(v => v.version === version);
          if (!versionData) return null;
          
          const percentage = ((versionData.count / latestData.totalDevices) * 100).toFixed(1);
          
          return (
            <div key={version} className="mx-2 d-flex align-items-center">
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: getVersionColor(version, index),
                  marginRight: '5px',
                  borderRadius: '2px'
                }}
              />
              <span>
                {formatVersion(version)} <small className="text-muted">({percentage}%)</small>
              </span>
            </div>
          );
        })}
        
        {/* Add "Other" if it exists in the data */}
        {showOther && latestData.versionData.some(v => v.version === 'Other') && (
          <div className="mx-2 d-flex align-items-center">
            <div
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: OTHER_COLOR,
                marginRight: '5px',
                borderRadius: '2px'
              }}
            />
            <span>
              Other <small className="text-muted">
                {(() => {
                  // Find the "Other" version in versionData
                  const otherData = latestData.versionData.find(v => v.version === 'Other');
                  const otherCount = otherData ? otherData.count : 0;
                  const percentage = ((otherCount / latestData.totalDevices) * 100).toFixed(1);
                  return `(${percentage}%)`;
                })()}
              </small>
            </span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center my-3">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && historicalData.length === 0) {
    return <div className="alert alert-danger">{error}</div>;
  }

  // Check if we have enough data
  const hasEnoughData = historicalData.length > 1;

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div>
          <h5 className="card-title mb-0">Firmware Version History</h5>
          <div className="text-muted small">Version distribution over time</div>
        </div>
      </div>
      <div className="card-body">
        {/* Time range selector */}
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div className="d-flex flex-wrap gap-1">
            {timeRanges.map(range => (
              <button 
                key={range.label}
                className={`btn btn-sm ${selectedRange.label === range.label 
                  ? 'btn-primary' 
                  : 'btn-outline-primary'}`}
                onClick={() => handleRangeChange(range)}
              >
                {range.label}
              </button>
            ))}
          </div>
          
          <div className="d-flex align-items-center gap-2">
            {/* <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="showOtherToggle"
                checked={showOther}
                onChange={(e) => setShowOther(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showOtherToggle">
                Show "Other"
              </label>
            </div> */}
            
            <div className="btn-group">
              {[3, 5, 7].map(count => (
                <button
                  key={count}
                  className={`btn btn-sm ${topVersionCount === count ? 'btn-secondary' : 'btn-outline-secondary'}`}
                  onClick={() => handleTopVersionsChange(count)}
                >
                  Top {count}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Legend */}
        {renderCustonLegend()}
        
        {!hasEnoughData ? (
          <div className="alert alert-info text-center py-5">
            <p><i className="fas fa-info-circle fa-2x mb-3"></i></p>
            <h5>Not Enough Historical Data</h5>
            <p className="mb-0">
              We need more data points to generate meaningful trends.
              <br />
              Check back later as more data is collected.
            </p>
          </div>
        ) : (
          <div style={{ height: '400px', width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={historicalData}
                margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  interval={getTickInterval()}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tickMargin={15}
                />
                <YAxis />
                <Tooltip formatter={tooltipFormatter} />
                <Legend />
                
                {/* Create a line for each version to display */}
                {displayVersions.map((version, index) => (
                  <Line
                    key={version}
                    type="monotone"
                    dataKey={version}
                    name={version}
                    stroke={getVersionColor(version, index)}
                    strokeWidth={2}
                    dot={{ r: 1 }}
                    activeDot={{ r: 5 }}
                  />
                ))}
                
                {/* Add "Other" line if it exists in the data */}
                {showOther && historicalData.length > 0 && historicalData.some(d => d.hasOwnProperty('Other')) && (
                  <Line
                    type="monotone"
                    dataKey="Other"
                    name="Other"
                    stroke={OTHER_COLOR}
                    strokeWidth={2}
                    dot={{ r: 1 }}
                    activeDot={{ r: 5 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="card-footer bg-light d-flex justify-content-between align-items-center">
        <span className="small text-muted">
          Showing top {topVersionCount} versions over {selectedRange.label.toLowerCase()}
        </span>
        <button 
          className="btn btn-sm btn-outline-secondary" 
          onClick={fetchVersionHistory}
          disabled={loading}
        >
          Refresh
        </button>
      </div>
    </div>
  );
};

export default FirmwareVersionHistory;